// src/pages/mock-data-management-dashboard/components/StatisticsSidebar.jsx
import React from 'react';
import Icon from 'components/AppIcon';
import { format } from 'date-fns';

const StatisticsSidebar = ({ statistics, mockData, onQuickAction }) => {
  const getDataBreakdown = () => {
    return [
      {
        label: 'Users',
        count: mockData.users.length,
        icon: 'Users',
        color: 'primary',
        percentage: mockData.users.length > 0 ? Math.round((mockData.users.length / statistics.totalRecords) * 100) : 0
      },
      {
        label: 'Biodata Records',
        count: mockData.biodataRecords.length,
        icon: 'FileText',
        color: 'success',
        percentage: mockData.biodataRecords.length > 0 ? Math.round((mockData.biodataRecords.length / statistics.totalRecords) * 100) : 0
      },
      {
        label: 'Documents',
        count: mockData.documents.length,
        icon: 'FileImage',
        color: 'warning',
        percentage: mockData.documents.length > 0 ? Math.round((mockData.documents.length / statistics.totalRecords) * 100) : 0
      },
      {
        label: 'System Logs',
        count: mockData.systemLogs.length,
        icon: 'Activity',
        color: 'accent',
        percentage: mockData.systemLogs.length > 0 ? Math.round((mockData.systemLogs.length / statistics.totalRecords) * 100) : 0
      }
    ];
  };

  const getColorClasses = (color) => {
    const colorMap = {
      primary: {
        bg: 'bg-primary-50',
        icon: 'text-primary-600',
        text: 'text-primary-700',
        progress: 'bg-primary-500'
      },
      success: {
        bg: 'bg-success-50',
        icon: 'text-success-600',
        text: 'text-success-700',
        progress: 'bg-success-500'
      },
      warning: {
        bg: 'bg-warning-50',
        icon: 'text-warning-600',
        text: 'text-warning-700',
        progress: 'bg-warning-500'
      },
      accent: {
        bg: 'bg-accent-50',
        icon: 'text-accent-600',
        text: 'text-accent-700',
        progress: 'bg-accent-500'
      }
    };
    return colorMap[color] || colorMap.primary;
  };

  const getQuickActions = () => {
    return [
      {
        id: 'generateSample',
        label: 'Generate Sample Data',
        description: 'Create a complete sample dataset',
        icon: 'Database',
        color: 'primary'
      },
      {
        id: 'resetAll',
        label: 'Reset All Data',
        description: 'Clear all generated data',
        icon: 'Trash2',
        color: 'error'
      }
    ];
  };

  const getRecentActivities = () => {
    const activities = [];
    
    if (statistics.lastGeneration) {
      activities.push({
        id: 'last_generation',
        action: 'Data Generated',
        description: `${statistics.totalRecords} records created`,
        timestamp: statistics.lastGeneration,
        icon: 'Plus',
        color: 'success'
      });
    }

    if (statistics.apiCalls > 0) {
      activities.push({
        id: 'api_calls',
        action: 'API Calls Made',
        description: `${statistics.apiCalls} requests processed`,
        timestamp: new Date(),
        icon: 'Zap',
        color: 'accent'
      });
    }

    // Add some sample activities if none exist
    if (activities.length === 0) {
      activities.push(
        {
          id: 'sample_1',
          action: 'Dashboard Initialized',
          description: 'Mock data management system ready',
          timestamp: new Date(),
          icon: 'CheckCircle',
          color: 'primary'
        }
      );
    }

    return activities.slice(0, 5); // Show only recent 5
  };

  const dataBreakdown = getDataBreakdown();
  const quickActions = getQuickActions();
  const recentActivities = getRecentActivities();

  return (
    <div className="p-6 space-y-6">
      {/* Statistics Overview */}
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-accent-600" />
          </div>
          <div>
            <h3 className="font-semibold text-text-primary">Statistics</h3>
            <p className="text-xs text-text-secondary">Data overview</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Total Records */}
          <div className="text-center p-4 bg-secondary-50 rounded-lg">
            <div className="text-2xl font-bold text-text-primary">
              {statistics.totalRecords.toLocaleString()}
            </div>
            <div className="text-sm text-text-secondary">Total Records</div>
          </div>

          {/* API Calls */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">API Calls</span>
            <span className="font-semibold text-text-primary">
              {statistics.apiCalls.toLocaleString()}
            </span>
          </div>

          {/* Data Integrity */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Data Integrity</span>
            <span className="font-semibold text-success-600">
              {statistics.dataIntegrity}%
            </span>
          </div>

          {/* Last Generation */}
          {statistics.lastGeneration && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-text-secondary">Last Generated</span>
              <span className="font-semibold text-text-primary text-xs">
                {format(statistics.lastGeneration, 'MMM dd, HH:mm')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Data Breakdown */}
      <div className="card p-6">
        <h3 className="font-semibold text-text-primary mb-4">Data Breakdown</h3>
        <div className="space-y-4">
          {dataBreakdown.map((item) => {
            const colorClasses = getColorClasses(item.color);
            return (
              <div key={item.label} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name={item.icon} size={14} className={colorClasses.icon} />
                    <span className="text-sm text-text-secondary">{item.label}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-text-primary text-sm">
                      {item.count.toLocaleString()}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {item.percentage}%
                    </div>
                  </div>
                </div>
                <div className="w-full bg-secondary-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${colorClasses.progress}`}
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="font-semibold text-text-primary mb-4">Quick Actions</h3>
        <div className="space-y-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => onQuickAction(action.id)}
              className={`w-full p-3 rounded-lg border text-left hover:bg-secondary-50 transition-colors ${
                action.color === 'error' ?'border-error-200 hover:border-error-300' :'border-secondary-200 hover:border-secondary-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon
                  name={action.icon}
                  size={16}
                  className={action.color === 'error' ? 'text-error-600' : 'text-primary-600'}
                />
                <div>
                  <div className={`text-sm font-medium ${
                    action.color === 'error' ? 'text-error-700' : 'text-text-primary'
                  }`}>
                    {action.label}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {action.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card p-6">
        <h3 className="font-semibold text-text-primary mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {recentActivities.map((activity) => {
            const colorClasses = getColorClasses(activity.color);
            return (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses.bg}`}>
                  <Icon name={activity.icon} size={14} className={colorClasses.icon} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary">
                    {activity.action}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {activity.description}
                  </div>
                  <div className="text-xs text-text-secondary mt-1">
                    {format(activity.timestamp, 'MMM dd, HH:mm')}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatisticsSidebar;